import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Release,
  ReleaseCondition,
  ReleaseConversation,
  ReleaseComment,
  Instruction,
  ReleaseConversationFeedback,
  FilterGroup,
  ReleaseResourceAccessStatus,
} from "../types";
import {
  useCreateNewRelease,
  useCreateSimulator,
  useGetReleases,
  useGetSimulatorById,
  useGetSimulators,
  usePublishRelease,
  useUpdateRelease,
  useUpdateSimulator,
} from "../../../api/useRelease";
//@ts-ignore
import confetti from "canvas-confetti";

import { useAuth0 } from "@auth0/auth0-react";
import { useParams } from "react-router-dom";
import { useWorkspace } from "./wrokspace";
import { useSimulatorTracking } from "../useSimulatorTracking";
import { Simulator, SimulatorChannel } from "@/api/simulator/simulator.model";
import { useGetSimulator, useGetSimulatorReleases } from "@/api/simulator";
import { useErrorModal } from "@/components/errorModal";
import { ExtendedRelease } from "@/api/simulator/simulator.api";

import { useMutation } from "react-query";
import { toast } from "sonner";
import { useReleaseApi } from "@/v2/entities/release/Release.api";

export const aiAgentNames = [
  "AI Agent Charlie",
  "Supportive Sam",
  "Helper Hannah",
  "Agent Alex",
  "Customer Care Casey",
  "Service Sarah",
  "Assistant Andy",
  "Problem Solver Pat",
  "Friendly Fran",
  "Guide Gabe",
  "Navigator Nate",
  "Responder Riley",
  "Solution Sage",
  "Troubleshooter Terry",
  "Aid Ava",
  "Support Specialist Spencer",
  "Helper Harper",
  "Customer Champion Chris",
  "Service Star Sky",
  "Agent Ace",
];

interface ReleaseContextType {
  loadingSimulators: boolean;
  simulators: {
    id: number;
    name: string;
    releases: Release[];
    channel: SimulatorChannel;
    publishedReleaseId?: number;
  }[];
  simulator?: {
    id: number;
    name: string;
    releases: Release[];
    channel: SimulatorChannel;
    publishedReleaseId?: number;
    conversations: Pick<
      ReleaseConversation,
      | "id"
      | "feedback"
      | "feedbackComment"
      | "createdAt"
      | "updatedAt"
      | "tenantId"
      | "topicId"
      | "releaseId"
    >[];
    seperateGoodFeedback: boolean;
    allowRegularAttachments: boolean;
    allowInlineAttachments: boolean;
    timeDifferenceLimit: number;
    reclassifyTopicOnSubsequentAnswers?: boolean;
  };
  fullSimulator?: {
    id: number;
    name: string;
    releases: Release[];
    channel: SimulatorChannel;
    publishedReleaseId?: number;
    conversations: ReleaseConversation[];
  };
  releases: Release[];
  relevantRelease: Release | null;
  setReleases: React.Dispatch<React.SetStateAction<Release[]>>;
  releaseConditions: ReleaseCondition[];
  setReleaseConditions: React.Dispatch<
    React.SetStateAction<ReleaseCondition[]>
  >;
  releaseConversations: ReleaseConversation[];
  releaseComments: ReleaseComment[];
  setReleaseComments: React.Dispatch<React.SetStateAction<ReleaseComment[]>>;
  handleUpdateReleaseInstructions: (instructions: any) => void;
  handleUpdateConversation: (object: {
    localConversationId: number;
    message: { role: "user" | "assistant"; content: string };
    topicId?: number;
    feedback: ReleaseConversationFeedback | "NONE" | "POSITIVE" | "NEGATIVE";
    feedbackComment?: string;
  }) => void;
  conversationId: number | null;
  releaseId: number | null;
  handlePublish: ({
    instructionsToPublish,
    filterPolicy,
    callback,
  }: {
    instructionsToPublish: Instruction[];
    filterPolicy: string;
    callback: () => void;
  }) => void;
  handleCreateNewRelease: (simulatorId: number, resourceAccess?: Record<string, ReleaseResourceAccessStatus>) => void;
  handleCreateSimulator: (channel?: SimulatorChannel, customName?: string, resourceAccess?: Record<string, ReleaseResourceAccessStatus>) => Promise<Simulator>;
  handleUpdateSimulator: (id: number, name: string) => Promise<void>;
  setChosenSimulatorId: (id: number) => void;
  chosenSimulatorId: number | null;
  refetchSimulators: () => Promise<unknown>;
  refetchSelectedSimulator: () => Promise<unknown>;
  isLoadingSimulator: boolean;
  handleUpdateFilterPolicy: (filterPolicy: string) => void;
  filterGroups: FilterGroup[];
  setFilterGroups: (filterGroups: FilterGroup[]) => void;
  handleUpdateReleasecustomToneOfVoice: (
    customToneOfVoice: string
  ) => Promise<void>;
  releasesWithConversations: ExtendedRelease[];
}

const ReleaseContext = createContext<ReleaseContextType | undefined>(undefined);

export const useReleases = (): ReleaseContextType => {
  const context = useContext(ReleaseContext);
  if (!context) {
    throw new Error("useReleases must be used within a ReleaseProvider");
  }

  return context;
};

export const ReleaseProvider: React.FC<{
  children: React.ReactNode;
  simulatorId?: string;
}> = ({ children, simulatorId: propSimulatorId }) => {
  const { trackSessionStarted } = useSimulatorTracking();
  const { user } = useAuth0();
  const { simulatorId: urlSimulatorId } = useParams();
  const simulatorId = propSimulatorId ?? urlSimulatorId;
  const { workspace } = useWorkspace();
  const releaseApi = useReleaseApi();

  const [releases, setReleases] = useState<Release[]>([]);
  const [releaseConditions, setReleaseConditions] = useState<
    ReleaseCondition[]
  >([]);
  const [releaseComments, setReleaseComments] = useState<ReleaseComment[]>([]);
  const [releaseId, setReleaseId] = useState(0);
  const [chosenSimulatorId, setChosenSimulatorId] = useState(0);
  const [conversationId, setConversationId] = useState<number | null>(null);
  const [filterGroups, setFilterGroups] = useState<FilterGroup[]>([]);
  const [lastKnownConversations, setLastKnownConversations] = useState<
    ReleaseConversation[]
  >([]);

  const {
    data: simulators,
    isLoading: loadingSimulators,
    refetch: refetchSimulators,
  } = useGetSimulators(!!user);

  const firstSimulatorId = useMemo(() => {
    if (!simulatorId && simulators?.length) {
      const autonomousTicketEventSimulator = simulators.find(
        (s: Simulator) =>
          s.channel === "autonomous_ticket_event" && s.releases.length
      );
      if (autonomousTicketEventSimulator) {
        return autonomousTicketEventSimulator.id;
      }
    }
    return simulatorId;
  }, [simulators, simulatorId]);

  const actualSimulatorId = simulatorId ?? firstSimulatorId;

  const {
    data: releasesWithConversationsData,
    refetch: refetchReleasesWithConversations,
  } = useGetSimulatorReleases(actualSimulatorId?.toString(), false, false);

  const { ErrorModal, openToggle } = useErrorModal();
  const { refetch } = useGetReleases(!!user && !!actualSimulatorId);
  const { mutateAsync: createNewRelease } = useCreateNewRelease();
  const { mutateAsync: createSimulator } = useCreateSimulator();
  const { mutateAsync: updateSimulator } = useUpdateSimulator({
    simulatorId: chosenSimulatorId,
  });
  const { mutateAsync: publishRelease, isError } = usePublishRelease(
    releaseId,
    +(simulatorId ?? 0)
  );

  const releasesWithConversations = useMemo(() => {
    if (
      !releasesWithConversationsData ||
      !Array.isArray(releasesWithConversationsData) ||
      (releasesWithConversationsData as any).type === "Error" ||
      (releasesWithConversationsData as any).statusCode === 500
    ) {
      return [];
    }
    return releasesWithConversationsData;
  }, [releasesWithConversationsData]);

  useEffect(() => {
    if (isError) {
      try {
        // window.quack?.proactiveTrigger();
      } catch (e) {}
      openToggle();
    }
  }, [isError]);

  useEffect(() => {
    if (actualSimulatorId) {
      setChosenSimulatorId(+actualSimulatorId);
    }
  }, [actualSimulatorId]);

  const { data: fullSimulator, refetch: fullSimulatorRefetch } =
    useGetSimulatorById(!!user, +(actualSimulatorId ?? 0));

  const handleCreateSimulator = async (
    channel: SimulatorChannel = "autonomous_ticket_event",
    customName?: string,
    resourceAccess?: Record<string, ReleaseResourceAccessStatus>
  ) => {
    const name = customName ?? aiAgentNames[Math.floor(Math.random() * aiAgentNames.length)];

    const res = await createSimulator({
      id: null,
      tenantId: workspace,
      name,
      channel,
    });

    handleCreateNewRelease(res.id, resourceAccess);
    trackSessionStarted("simulator_created");

    return res;
  };

  const handleUpdateSimulator = async (simulatorId: number, name: string) => {
    await updateSimulator({
      id: simulatorId,
      tenantId: workspace,
      name: name,
    });
    refetchSimulators();
    refetchReleasesWithConversations();
  };

  const handleCreateNewRelease = (simulatorId: number, resourceAccess?: Record<string, ReleaseResourceAccessStatus>) => {
    // Map dialog resourceAccess to API structure
    // snippets -> snippets
    // articles -> externalArticles and internalArticles
    const mappedResourceAccess = resourceAccess
      ? {
          snippets: resourceAccess.snippets ?? ReleaseResourceAccessStatus.FULL_ACCESS,
          externalArticles: resourceAccess.articles ?? ReleaseResourceAccessStatus.FULL_ACCESS,
          internalArticles: resourceAccess.wiki ?? ReleaseResourceAccessStatus.FULL_ACCESS,
        }
      : {
          snippets: ReleaseResourceAccessStatus.FULL_ACCESS,
          externalArticles: ReleaseResourceAccessStatus.FULL_ACCESS,
          internalArticles: ReleaseResourceAccessStatus.FULL_ACCESS,
        };

    createNewRelease({
      id: null,
      simulatorId,
      releasePolicy: JSON.stringify([
        {
          content: 2,
          subTopic: "",
          topic: "maxBackAndForth",
          type: "general",
        },
        {
          content: "medium",
          subTopic: "",
          topic: "length",
          type: "general",
        },
        {
          content: true,
          subTopic: "",
          topic: "includeLink",
          type: "general",
        },
        {
          content: true,
          subTopic: "",
          topic: "includeOpening",
          type: "general",
        },
      ]),
      tenantId: workspace,
      status: "draft",
      gradualRolloutEnabled: true,
      gradualRolloutPercentage: 100,
      resourceAccess: mappedResourceAccess,
    }).then(() => {
      refetch();
      refetchSimulators();
      fullSimulatorRefetch();
      refetchReleasesWithConversations();
    });
  };

  const relevantRelease = useMemo(() => {
    if (releaseId) {
      return releases?.filter((r) => r.id === +releaseId)?.[0];
    } else {
      return null;
    }
  }, [releases, releaseId]);

  const { mutateAsync: upsertRelease } = useUpdateRelease(
    relevantRelease?.id ?? 0
  );

  const { mutateAsync: upsertConversation } = useMutation({
    mutationFn: releaseApi.upsertConversation,
    onError: () => {
      toast.error("Failed to update conversation");
    },
  });

  useEffect(() => {
    if (simulators) {
      const chosenSimulator = simulators.find(
        (s: { id: number }) => s.id === +(actualSimulatorId ?? 0)
      );
      setReleases(chosenSimulator?.releases ?? []);

      // Find the draft release first, if not found use the published release
      const draftRelease = chosenSimulator?.releases.find(
        (release: Release) => release.status === "draft" && !release.publishedAt
      );

      const publishedRelease = chosenSimulator?.releases.find(
        (release: Release) => release.id === chosenSimulator.publishedReleaseId
      );

      setReleaseId(draftRelease?.id ?? publishedRelease?.id ?? 0);
    }
  }, [user, simulators, actualSimulatorId]);

  const handleUpdateReleaseInstructions = (instructions: any) => {
    upsertRelease({
      id: relevantRelease?.id ?? 0,
      releasePolicy: JSON.stringify(instructions),
      tenantId: user!.owner,
      status: "draft",
      simulatorId: +(actualSimulatorId ?? 0),
    });
  };

  const handleUpdateFilterPolicy = (filterPolicy: string) => {
    upsertRelease({
      id: relevantRelease?.id ?? 0,
      filterPolicy,
      tenantId: user!.owner,
      simulatorId: +(actualSimulatorId ?? 0),
    });
  };

  const handleUpdateReleasecustomToneOfVoice = async (
    customToneOfVoice: string
  ) => {
    await upsertRelease({
      id: relevantRelease?.id ?? 0,
      customToneOfVoice: customToneOfVoice,
      tenantId: user!.owner,
      simulatorId: +(actualSimulatorId ?? 0),
    });
    refetchSimulators();
    refetchReleasesWithConversations();
  };

  const handleUpdateConversation = ({
    localConversationId,
    message,
    topicId,
    feedback,
    feedbackComment,
  }: {
    localConversationId: number;
    message: { role: "user" | "assistant"; content: string };
    topicId?: number;
    feedback: ReleaseConversationFeedback | "NONE" | "POSITIVE" | "NEGATIVE";
    feedbackComment?: string;
  }) => {
    if (relevantRelease?.id == null) {
      return;
    }

    upsertConversation({
      releaseConversationId: localConversationId ?? 0,
      comment: message.content,
      topicId: topicId ?? undefined,
      feedback: feedback,
      isAgent: message.role === "assistant",
      feedbackComment: "",
      releaseId: relevantRelease?.id,
      simulatorId: chosenSimulatorId,
    }).then((res) => {
      if (res == null) {
        return;
      }

      refetch();
      fullSimulatorRefetch();
      refetchReleasesWithConversations();
      setConversationId(res.id);
    });
  };

  const handlePublish = async ({
    instructionsToPublish,
    filterPolicy,
    callback,
  }: {
    instructionsToPublish: Instruction[];
    filterPolicy: string;
    callback: () => void;
  }) => {
    try {
      await publishRelease({
        id: releaseId,
        releasePolicy: JSON.stringify(instructionsToPublish),
        filterPolicy,
        tenantId: user!.owner,
        simulatorId: +(simulatorId ?? 0),
        customToneOfVoice: relevantRelease?.customToneOfVoice,
      });
      refetch();
      refetchSimulators();
      fullSimulatorRefetch();
      refetchReleasesWithConversations();
      callback();
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 },
      });
    } catch (error) {
      console.error(error);
    }
  };

  const releaseConversations = useMemo(() => {
    if (
      !releasesWithConversations ||
      !Array.isArray(releasesWithConversations)
    ) {
      return lastKnownConversations;
    }
    const draftRelease = releasesWithConversations?.find(
      (r) => r.status === "draft" && !r.publishedAt
    );
    if (!draftRelease) {
      return lastKnownConversations;
    }
    const conversations = draftRelease.conversations || [];
    const sortedConversations = conversations.sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );

    if (sortedConversations.length > 0) {
      setLastKnownConversations(sortedConversations);
    }

    return sortedConversations.length > 0
      ? sortedConversations
      : lastKnownConversations;
  }, [releasesWithConversations, lastKnownConversations]);

  const simulator = useMemo(() => {
    if (!simulators || !Array.isArray(simulators)) {
      return null;
    }
    return simulators?.find(
      (s: { id: number }) => s.id === +(chosenSimulatorId ?? 0)
    );
  }, [simulators, chosenSimulatorId]);

  return (
    <ReleaseContext.Provider
      value={{
        releases,
        setReleases,
        releaseConditions,
        setReleaseConditions,
        releaseConversations,
        releaseComments,
        setReleaseComments,
        relevantRelease,
        handleUpdateReleaseInstructions,
        handleUpdateConversation,
        conversationId,
        handlePublish,
        handleCreateNewRelease,
        loadingSimulators,
        simulators,
        simulator,
        releaseId,
        handleCreateSimulator,
        handleUpdateSimulator,
        setChosenSimulatorId,
        chosenSimulatorId,
        refetchSimulators,
        refetchSelectedSimulator: fullSimulatorRefetch,
        fullSimulator,
        isLoadingSimulator: loadingSimulators,
        handleUpdateFilterPolicy,
        filterGroups,
        setFilterGroups,
        handleUpdateReleasecustomToneOfVoice,
        releasesWithConversations: releasesWithConversations ?? [],
      }}
    >
      <>
        {children}
        <ErrorModal
          title="Your version is outdated"
          description="Seems like you are using an older version of the AI Agent. Please refresh the page to get the latest version. We automatically sent a full report your issue to our team, we will reach out to you as soon as possible."
          actionText="OK"
          onAction={() => {
            window.location.reload();
          }}
        />
      </>
    </ReleaseContext.Provider>
  );
};
